import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { auth } from "@/lib/firebase";
import { dualWriteJob } from "@/lib/firestore-helpers";
import { usePermitTypes, useJobScopes, type PermitType, type JobScope } from "@/hooks/useCompliance";
import { sanitizeAndTrim, sanitizeEmail, sanitizePhone } from "@/lib/sanitize";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

type Job = {
  id: string;
  title: string;
  description: string;
  location: string;
  status: "not_started" | "in_progress" | "completed" | "pending_review";
  client_name: string;
  client_email: string;
  client_phone: string;
  complex_name: string;
  unit_number: string;
  permitType?: string | null;
  jobScope?: string | null;
  jobScopeOther?: string;
  projectType?: 'residential' | 'commercial';
};

type EditJobProps = {
  job: Job;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

// Map permit type codes to trade types for job scope filtering
const PERMIT_TO_TRADE: Record<string, string> = {
  electrical: "electrical",
  plumbing: "plumbing",
  mechanical: "hvac",
  building: "general",
  roofing: "roofing",
};

export const EditJob = ({ job, open, onOpenChange, onSuccess }: EditJobProps) => {
  const [formData, setFormData] = useState({
    title: job.title,
    description: job.description || "",
    location: job.location || "",
    client_name: job.client_name || "",
    client_email: job.client_email || "",
    client_phone: job.client_phone || "",
    complex_name: job.complex_name || "",
    unit_number: job.unit_number || "",
    permitType: job.permitType || "",
    jobScope: job.jobScope || "",
    jobScopeOther: job.jobScopeOther || "",
    projectType: job.projectType || "residential" as 'residential' | 'commercial',
  });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  // Fetch permit types and job scopes from Supabase
  const { data: permitTypes, isLoading: loadingPermitTypes } = usePermitTypes();

  // Get the trade type for the selected permit type
  const tradeType = formData.permitType ? PERMIT_TO_TRADE[formData.permitType] : undefined;
  const { data: allJobScopes, isLoading: loadingJobScopes } = useJobScopes(tradeType);

  // Filter job scopes based on selected permit type's trade
  const availableScopes = allJobScopes || [];

  // Reset form when job changes (e.g., dialog reopens)
  useEffect(() => {
    if (open) {
      setFormData({
        title: job.title,
        description: job.description || "",
        location: job.location || "",
        client_name: job.client_name || "",
        client_email: job.client_email || "",
        client_phone: job.client_phone || "",
        complex_name: job.complex_name || "",
        unit_number: job.unit_number || "",
        permitType: job.permitType || "",
        jobScope: job.jobScope || "",
        jobScopeOther: job.jobScopeOther || "",
        projectType: job.projectType || "residential",
      });
    }
  }, [open, job]);

  const handlePermitTypeChange = (value: string) => {
    setFormData({
      ...formData,
      permitType: value,
      jobScope: "", // Reset job scope when permit type changes
      jobScopeOther: "",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Get current user as contractor
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Not authenticated");
      }

      // SECURITY: Sanitize all user input before saving
      const sanitizedData = {
        title: sanitizeAndTrim(formData.title, 200),
        description: sanitizeAndTrim(formData.description, 2000),
        location: sanitizeAndTrim(formData.location, 500),
        client_name: sanitizeAndTrim(formData.client_name, 100),
        client_email: formData.client_email ? sanitizeEmail(formData.client_email) : "",
        client_phone: formData.client_phone ? sanitizePhone(formData.client_phone) : "",
        complex_name: sanitizeAndTrim(formData.complex_name, 200),
        unit_number: sanitizeAndTrim(formData.unit_number, 50),
        permitType: formData.permitType, // Select values don't need sanitization
        jobScope: formData.jobScope,     // Select values don't need sanitization
        jobScopeOther: sanitizeAndTrim(formData.jobScopeOther, 500),
        projectType: formData.projectType, // Select value
      };

      // Use dual-write to update job in both structures
      await dualWriteJob(currentUser.uid, job.id, sanitizedData);

      toast({
        title: "Success",
        description: "Permit updated successfully",
      });
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error updating permit",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const isLoadingData = loadingPermitTypes || loadingJobScopes;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Permit</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Permit Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="projectType">Project Type</Label>
            <Select
              value={formData.projectType}
              onValueChange={(value) => setFormData({ ...formData, projectType: value as 'residential' | 'commercial' })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select project type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="residential">Residential</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="permitType">Permit Type</Label>
              <Select
                value={formData.permitType}
                onValueChange={handlePermitTypeChange}
                disabled={loadingPermitTypes}
              >
                <SelectTrigger>
                  {loadingPermitTypes ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Loading...</span>
                    </div>
                  ) : (
                    <SelectValue placeholder="Select type..." />
                  )}
                </SelectTrigger>
                <SelectContent>
                  {permitTypes?.map((type: PermitType) => (
                    <SelectItem key={type.code} value={type.code}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="jobScope">Scope of Work</Label>
              <Select
                value={formData.jobScope}
                onValueChange={(value) => setFormData({ ...formData, jobScope: value })}
                disabled={!formData.permitType || loadingJobScopes}
              >
                <SelectTrigger>
                  {loadingJobScopes && formData.permitType ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Loading...</span>
                    </div>
                  ) : (
                    <SelectValue placeholder={formData.permitType ? "Select scope..." : "Select permit type first"} />
                  )}
                </SelectTrigger>
                <SelectContent>
                  {availableScopes.map((scope: JobScope) => (
                    <SelectItem key={scope.code} value={scope.code}>
                      {scope.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {formData.jobScope === "other" && (
            <div className="space-y-2">
              <Label htmlFor="jobScopeOther">Describe the work</Label>
              <Input
                id="jobScopeOther"
                value={formData.jobScopeOther}
                onChange={(e) => setFormData({ ...formData, jobScopeOther: e.target.value })}
                placeholder="Describe the type of work..."
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="complex_name">Complex Name</Label>
              <Input
                id="complex_name"
                value={formData.complex_name}
                onChange={(e) => setFormData({ ...formData, complex_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit_number">Unit Number</Label>
              <Input
                id="unit_number"
                value={formData.unit_number}
                onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="client_name">Client Name</Label>
            <Input
              id="client_name"
              value={formData.client_name}
              onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="client_email">Client Email</Label>
            <Input
              id="client_email"
              type="email"
              value={formData.client_email}
              onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="client_phone">Client Phone</Label>
            <Input
              id="client_phone"
              type="tel"
              value={formData.client_phone}
              onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
            />
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || isLoadingData}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
